import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, FileText,
  ChevronRight, ChevronDown, Shield, School, MapPin, UserCheck,
  Users, BookOpen, GraduationCap, Search, Save, X, AlertTriangle,
  Play, Layers, Eye, EyeOff, Loader2, ArrowLeft, HelpCircle, History, FileUp,
  Film
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../../../api/client';
import { Modal } from '../../../../../components/ui/Modal';
import { ConfirmModal } from '../../../../../components/ui/ConfirmModal';

import type { Perfil, Topico, Subtopico, EadMaterial } from '../../types';
import { PERFIL_INFO } from '../../constants';
import { getPerfilIcon, getPerfilLabel, getPerfilDesc } from '../../utils/perfil-helpers';
import { useEcadDragDrop } from './hooks/useEcadDragDrop';
import { PerfilModal } from './components/Modals/PerfilModal';
import { TopicoModal } from './components/Modals/TopicoModal';
import { AulaModal } from './components/Modals/AulaModal';
import { VersoesModal } from './components/Modals/VersoesModal';
import { VincularVideoModal } from './components/Modals/VincularVideoModal';



export function EcadStudioPageModular() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState<Perfil[]>([]);
  const [selectedPerfilId, setSelectedPerfilId] = useState<string>('professores');
  const [expandedTopicoId, setExpandedTopicoId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modais de Perfil
  const [perfilModalOpen, setPerfilModalOpen] = useState(false);
  const [editingPerfil, setEditingPerfil] = useState<Perfil | null>(null);
  const [perfilForm, setPerfilForm] = useState({ id: '', nome: '', descricao: '', ordem: 0, isInterno: false });

  // Modais de Tópico
  const [topicoModalOpen, setTopicoModalOpen] = useState(false);
  const [editingTopico, setEditingTopico] = useState<Topico | null>(null);
  const [topicoForm, setTopicoForm] = useState({ titulo: '', ordem: 0 });

  // Modais de Subtópico (Aula)
  const [aulaModalOpen, setAulaModalOpen] = useState(false);
  const [editingAula, setEditingAula] = useState<Subtopico | null>(null);
  const [targetTopicoId, setTargetTopicoId] = useState<string>('');

  // Histórico de versões
  const [versoesModalOpen, setVersoesModalOpen] = useState(false);
  const [versoesSubtopicoId, setVersoesSubtopicoId] = useState<string>('');
  const [versoesSubtopicoTitulo, setVersoesSubtopicoTitulo] = useState<string>('');
  const [versoesList, setVersoesList] = useState<any[]>([]);
  const [loadingVersoes, setLoadingVersoes] = useState(false);
  const [restoringVersao, setRestoringVersao] = useState<number | null>(null);
  const [publishingAulaId, setPublishingAulaId] = useState<string | null>(null);

  const [aulaForm, setAulaForm] = useState({
    titulo: '',
    descricao: '',
    ordem: 0,
    duracao: '5m 00s',
    dificuldade: 'Básico',
    status: 'PUBLICADO',
    videoUrl: '',
    pdfUrl: ''
  });

  // Estados adicionais para recursos premium (uploads e desativar PDF)
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [disablePdf, setDisablePdf] = useState(false);

  // Vídeos não vinculados e formulário de vinculação
  const [unlinkedVideos, setUnlinkedVideos] = useState<Array<{ filename: string; url: string; path: string }>>([]);
  const [vincularModalOpen, setVincularModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{ filename: string; url: string; path: string } | null>(null);
  const [vincularForm, setVincularForm] = useState({ perfilId: '', topicoId: '', aulaId: '' });
  const [vincularLoading, setVincularLoading] = useState(false);
  const [selectedVideoPaths, setSelectedVideoPaths] = useState<string[]>([]);


  // Modal de Confirmação de Exclusão
  const [deleteConfirm, setDeleteConfirm] = useState<{
    tipo: 'PERFIL' | 'TOPICO' | 'AULA';
    id: string;
    titulo: string;
  } | null>(null);

  // Auxiliares
  const generateUuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const loadCatalog = async () => {
    try {
      setLoading(true);
      const res = await api.get('/ecad/catalogo?includeEmpty=true');
      if (res.data && res.data.ok) {
        const rawCatalog = res.data.catalog || [];
        setCatalog(rawCatalog);
        
        if (rawCatalog.length > 0 && !rawCatalog.find((p: any) => p.id === selectedPerfilId)) {
          setSelectedPerfilId(rawCatalog[0].id);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar catálogo do EAD', err);
      toast.error('Erro ao carregar catálogo. Tente atualizar a página.');
    } finally {
      setLoading(false);
    }
  };

  const loadUnlinkedVideos = async () => {
    try {
      const res = await api.get('/ecad/unlinked-videos');
      if (res.data && res.data.ok) {
        setUnlinkedVideos(res.data.videos || []);
      }
    } catch (err) {
      console.error('Erro ao buscar vídeos não vinculados', err);
    }
  };

  const handleOpenVincularModal = (video: { filename: string; url: string; path: string }) => {
    setSelectedVideo(video);
    setVincularForm({ perfilId: '', topicoId: '', aulaId: '' });
    setVincularModalOpen(true);
  };

  const handleCloseVincularModal = () => {
    setSelectedVideo(null);
    setVincularModalOpen(false);
  };

  const handleConfirmVincular = async () => {
    if (!selectedVideo || !vincularForm.aulaId) return;
    setVincularLoading(true);
    try {
      const res = await api.post('/ecad/vincular-video', {
        subtopicoId: vincularForm.aulaId,
        videoPath: selectedVideo.path
      });
      if (res.data && res.data.ok) {
        toast.success('Vídeo vinculado com sucesso!');
        setVincularModalOpen(false);
        setSelectedVideo(null);
        loadCatalog();
        loadUnlinkedVideos();
      } else {
        toast.error('Erro ao vincular o vídeo.');
      }
    } catch (err) {
      console.error('Erro ao vincular vídeo', err);
      toast.error('Erro ao comunicar com o servidor.');
    } finally {
      setVincularLoading(false);
    }
  };

  const handleToggleSelectVideo = (path: string) => {
    setSelectedVideoPaths((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedVideoPaths.length === unlinkedVideos.length) {
      setSelectedVideoPaths([]);
    } else {
      setSelectedVideoPaths(unlinkedVideos.map((v) => v.path));
    }
  };

  const handleDeleteSelectedVideos = async () => {
    if (selectedVideoPaths.length === 0) return;
    if (!window.confirm(`Deseja realmente excluir permanentemente as ${selectedVideoPaths.length} gravações selecionadas?`)) {
      return;
    }

    try {
      const res = await api.post('/ecad/delete-videos', {
        videoPaths: selectedVideoPaths
      });
      if (res.data && res.data.ok) {
        toast.success(`${res.data.deletedCount} gravações excluídas com sucesso.`);
        setSelectedVideoPaths([]);
        loadUnlinkedVideos();
      } else {
        toast.error('Erro ao excluir gravações.');
      }
    } catch (err) {
      console.error('Erro ao excluir gravações', err);
      toast.error('Erro ao comunicar com o servidor.');
    }
  };

  const openVersoesModal = async (subtopicoId: string, titulo: string) => {
    setVersoesSubtopicoId(subtopicoId);
    setVersoesSubtopicoTitulo(titulo);
    setVersoesModalOpen(true);
    setLoadingVersoes(true);
    try {
      const res = await api.get(`/ecad/roteiro/${subtopicoId}/versoes`);
      if (res.data && res.data.ok) {
        setVersoesList(res.data.versoes || []);
      } else {
        toast.error('Erro ao obter histórico de versões.');
      }
    } catch (err) {
      console.error('Erro ao carregar histórico de versões:', err);
      toast.error('Falha ao comunicar com o servidor.');
    } finally {
      setLoadingVersoes(false);
    }
  };

  const handleRestoreVersao = async (versaoNum: number) => {
    if (!window.confirm(`Deseja realmente restaurar o roteiro "${versoesSubtopicoTitulo}" para a Versão ${versaoNum}? Os passos de automação e mídias atuais serão substituídos pelos desta versão.`)) {
      return;
    }
    setRestoringVersao(versaoNum);
    try {
      const res = await api.post(`/ecad/roteiro/${versoesSubtopicoId}/versoes/${versaoNum}/restaurar`);
      if (res.data && res.data.ok) {
        toast.success(`Versão ${versaoNum} restaurada com sucesso!`);
        setVersoesModalOpen(false);
        loadCatalog();
      } else {
        toast.error('Erro ao restaurar versão.');
      }
    } catch (err) {
      console.error('Erro ao restaurar versão', err);
      toast.error('Falha ao restaurar versão no servidor.');
    } finally {
      setRestoringVersao(null);
    }
  };

  const handlePublishAula = async (subtopicoId: string, titulo: string) => {
    if (!window.confirm(`Deseja realmente publicar o roteiro "${titulo}" e todos os seus passos e arquivos locais para o ambiente de Produção?`)) {
      return;
    }
    setPublishingAulaId(subtopicoId);
    try {
      const res = await api.post(`/ecad/roteiro/${subtopicoId}/publicar-remoto`);
      if (res.data && res.data.ok) {
        toast.success(`Roteiro "${titulo}" publicado com sucesso na produção!`);
      } else {
        toast.error('Erro ao publicar roteiro.');
      }
    } catch (err: any) {
      console.error('Erro ao publicar roteiro', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Falha ao comunicar com o servidor.';
      toast.error(`Falha ao publicar: ${errMsg}`);
    } finally {
      setPublishingAulaId(null);
    }
  };

  useEffect(() => {
    loadCatalog();
    loadUnlinkedVideos();
  }, []);

  // Filtra e organiza os dados do perfil selecionado
  const activePerfil = useMemo(() => {
    const perf = catalog.find((p) => p.id === selectedPerfilId) as Perfil | null;
    if (!perf) return null;

    const filteredTopicos = (perf.topicos || [])
      .map((t) => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return t;

        const subFiltered = (t.subtopicos || []).filter(
          (s) =>
            s.titulo.toLowerCase().includes(query) ||
            (s.descricao || '').toLowerCase().includes(query)
        );

        const matchTitle = t.titulo.toLowerCase().includes(query);

        if (matchTitle || subFiltered.length > 0) {
          return { ...t, subtopicos: subFiltered };
        }
        return null;
      })
      .filter((t): t is Topico => t !== null)
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));

    return {
      ...perf,
      topicos: filteredTopicos,
    };
  }, [catalog, selectedPerfilId, searchQuery]);

  // Expandir automaticamente o primeiro tópico filtrado
  useEffect(() => {
    if (activePerfil && activePerfil.topicos.length > 0 && !expandedTopicoId) {
      setExpandedTopicoId(activePerfil.topicos[0].id);
    }
  }, [activePerfil]);

  // Salvar Perfil
  const handleSavePerfil = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!perfilForm.nome.trim()) {
      toast.error('O nome do perfil é obrigatório.');
      return;
    }
    const perfilId = editingPerfil ? editingPerfil.id : perfilForm.id.trim();
    if (!perfilId) {
      toast.error('O ID do perfil é obrigatório.');
      return;
    }

    try {
      await api.post('/ecad/perfil', {
        id: perfilId,
        nome: perfilForm.nome,
        descricao: perfilForm.descricao,
        ordem: Number(perfilForm.ordem),
        isInterno: perfilForm.isInterno
      });

      toast.success(editingPerfil ? 'Perfil editado com sucesso!' : 'Perfil cadastrado com sucesso!');
      setPerfilModalOpen(false);
      setEditingPerfil(null);
      loadCatalog();
    } catch (err) {
      console.error('Erro ao salvar perfil', err);
      toast.error('Erro ao salvar perfil no servidor.');
    }
  };

  // Salvar Tópico (Módulo)
  const handleSaveTopico = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topicoForm.titulo.trim()) {
      toast.error('O título do módulo é obrigatório.');
      return;
    }

    try {
      const topicoId = editingTopico ? editingTopico.id : `${selectedPerfilId}_topico_${generateUuid().substring(0, 8)}`;
      await api.post('/ecad/topico', {
        id: topicoId,
        perfilId: selectedPerfilId,
        titulo: topicoForm.titulo,
        ordem: Number(topicoForm.ordem)
      });

      toast.success(editingTopico ? 'Módulo editado com sucesso!' : 'Módulo criado com sucesso!');
      setTopicoModalOpen(false);
      setEditingTopico(null);
      loadCatalog();
    } catch (err) {
      console.error('Erro ao salvar tópico', err);
      toast.error('Erro ao salvar módulo no servidor.');
    }
  };

  // Salvar Aula (Subtópico)
  const handleSaveAula = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!aulaForm.titulo.trim()) {
      toast.error('O título da aula é obrigatório.');
      return;
    }

    try {
      const aulaId = editingAula ? editingAula.id : `aula_${generateUuid()}`;
      await api.post('/ecad/roteiro', {
        id: aulaId,
        topicoId: targetTopicoId,
        titulo: aulaForm.titulo,
        descricao: aulaForm.descricao,
        ordem: Number(aulaForm.ordem),
        duracao: aulaForm.duracao || '5m 00s',
        dificuldade: aulaForm.dificuldade || 'Básico',
        status: aulaForm.status || 'PUBLICADO',
        videoUrl: aulaForm.videoUrl || undefined,
        pdfUrl: disablePdf ? undefined : (aulaForm.pdfUrl || undefined)
      });

      toast.success(editingAula ? 'Aula editada com sucesso!' : 'Aula cadastrada com sucesso!');
      setAulaModalOpen(false);
      setEditingAula(null);
      loadCatalog();
    } catch (err) {
      console.error('Erro ao salvar aula', err);
      toast.error('Erro ao salvar aula no servidor.');
    }
  };

  // Exclusão definitiva de perfil, tópico ou aula
  const executeDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.tipo === 'PERFIL') {
        await api.delete(`/ecad/perfil/${deleteConfirm.id}`);
        toast.success('Perfil excluído com sucesso (Cascade).');
        setSelectedPerfilId('professores'); // fallback
      } else if (deleteConfirm.tipo === 'TOPICO') {
        await api.delete(`/ecad/topico/${deleteConfirm.id}`);
        toast.success('Módulo excluído com sucesso (Soft Delete).');
      } else {
        await api.delete(`/ecad/roteiro/${deleteConfirm.id}`);
        toast.success('Aula excluída com sucesso (Soft Delete).');
      }
      setDeleteConfirm(null);
      loadCatalog();
    } catch (err) {
      console.error('Erro ao excluir item', err);
      toast.error('Erro ao excluir registro do servidor.');
    }
  };

  // Lógica de Upload de arquivos
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, tipo: 'VIDEO' | 'PDF') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Limites de tamanho recomendados (ex: 200MB para vídeo, 20MB para manual)
    const maxSize = tipo === 'VIDEO' ? 200 * 1024 * 1024 : 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`O arquivo excede o limite máximo de ${tipo === 'VIDEO' ? '200MB' : '20MB'}.`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('tipo', tipo);

    if (tipo === 'VIDEO') setUploadingVideo(true);
    else setUploadingPdf(true);

    try {
      const res = await api.post('/ecad/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (res.data && res.data.ok) {
        const finalUrl = res.data.url;
        setAulaForm((prev) => ({
          ...prev,
          [tipo === 'VIDEO' ? 'videoUrl' : 'pdfUrl']: finalUrl,
        }));
        toast.success(`${tipo === 'VIDEO' ? 'Vídeo' : 'PDF'} enviado com sucesso!`);
      } else {
        toast.error(`Erro ao fazer upload do ${tipo === 'VIDEO' ? 'vídeo' : 'PDF'}`);
      }
    } catch (err) {
      console.error('Erro no upload', err);
      toast.error(`Falha ao conectar com o servidor para upload.`);
    } finally {
      if (tipo === 'VIDEO') setUploadingVideo(false);
      else setUploadingPdf(false);
    }
  };

    const {
    draggedPerfilId,
    draggedTopicoId,
    draggedAula,
    handlePerfilDragStart,
    handlePerfilDragOver,
    handlePerfilDrop,
    handleTopicoDragStart,
    handleTopicoDragOver,
    handleTopicoDrop,
    handleAulaDragStart,
    handleAulaDragOver,
    handleAulaDrop,
  } = useEcadDragDrop({
    catalog,
    setCatalog,
    selectedPerfilId,
    activePerfil,
    loadCatalog,
  });

  // Funções para carregar formulários
  const openNewPerfil = () => {
    setEditingPerfil(null);
    setPerfilForm({ id: '', nome: '', descricao: '', ordem: catalog.length + 1, isInterno: false });
    setPerfilModalOpen(true);
  };

  const openEditPerfil = (p: Perfil) => {
    setEditingPerfil(p);
    setPerfilForm({
      id: p.id,
      nome: p.nome,
      descricao: (p as any).descricao || '',
      ordem: p.ordem || 0,
      isInterno: p.isInterno
    });
    setPerfilModalOpen(true);
  };

  const openNewTopico = () => {
    setEditingTopico(null);
    setTopicoForm({ titulo: '', ordem: (activePerfil?.topicos?.length || 0) + 1 });
    setTopicoModalOpen(true);
  };

  const openEditTopico = (t: Topico) => {
    setEditingTopico(t);
    setTopicoForm({ titulo: t.titulo, ordem: t.ordem ?? 0 });
    setTopicoModalOpen(true);
  };

  const openNewAula = (topicoId: string, subtopicosLength: number) => {
    setEditingAula(null);
    setTargetTopicoId(topicoId);
    setDisablePdf(false);
    setAulaForm({
      titulo: '',
      descricao: '',
      ordem: subtopicosLength + 1,
      duracao: '5m 00s',
      dificuldade: 'Básico',
      status: 'PUBLICADO',
      videoUrl: '',
      pdfUrl: ''
    });
    setAulaModalOpen(true);
  };

  const openEditAula = (a: Subtopico, topicoId: string) => {
    setEditingAula(a);
    setTargetTopicoId(topicoId);
    setDisablePdf(!a.pdfUrl);
    setAulaForm({
      titulo: a.titulo,
      descricao: a.descricao || '',
      ordem: a.ordem ?? 0,
      duracao: a.duracao || '5m 00s',
      dificuldade: a.dificuldade || 'Básico',
      status: a.status || 'PUBLICADO',
      videoUrl: a.videoUrl || '',
      pdfUrl: a.pdfUrl || ''
    });
    setAulaModalOpen(true);
  };

  // Mover ordem do tópico
  const moveTopicoOrdem = async (topico: Topico, direcao: 'UP' | 'DOWN') => {
    const list = activePerfil?.topicos || [];
    const idx = list.findIndex((t) => t.id === topico.id);
    if (idx === -1) return;

    const targetIdx = direcao === 'UP' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const swapTarget = list[targetIdx];
    try {
      await api.post('/ecad/topico', {
        id: topico.id,
        perfilId: topico.perfilId,
        titulo: topico.titulo,
        ordem: swapTarget.ordem
      });
      await api.post('/ecad/topico', {
        id: swapTarget.id,
        perfilId: swapTarget.perfilId,
        titulo: swapTarget.titulo,
        ordem: topico.ordem
      });
      loadCatalog();
    } catch {
      toast.error('Erro ao reordenar módulo.');
    }
  };

  // Mover ordem da aula
  const moveAulaOrdem = async (aula: Subtopico, topicoId: string, direcao: 'UP' | 'DOWN') => {
    const topico = activePerfil?.topicos?.find((t) => t.id === topicoId);
    if (!topico) return;

    const list = topico.subtopicos || [];
    const idx = list.findIndex((s) => s.id === aula.id);
    if (idx === -1) return;

    const targetIdx = direcao === 'UP' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;

    const swapTarget = list[targetIdx];
    try {
      await api.post('/ecad/roteiro', {
        id: aula.id,
        topicoId,
        titulo: aula.titulo,
        descricao: aula.descricao || '',
        ordem: swapTarget.ordem,
        duracao: aula.duracao,
        dificuldade: aula.dificuldade,
        status: aula.status,
        videoUrl: aula.videoUrl,
        pdfUrl: aula.pdfUrl
      });
      await api.post('/ecad/roteiro', {
        id: swapTarget.id,
        topicoId,
        titulo: swapTarget.titulo,
        descricao: swapTarget.descricao || '',
        ordem: aula.ordem,
        duracao: swapTarget.duracao,
        dificuldade: swapTarget.dificuldade,
        status: swapTarget.status,
        videoUrl: swapTarget.videoUrl,
        pdfUrl: swapTarget.pdfUrl
      });
      loadCatalog();
    } catch {
      toast.error('Erro ao reordenar aula.');
    }
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '450px', background: 'var(--bg-card)' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          <Loader2 size={40} className="animate-spin" style={{ margin: '0 auto 12px auto', color: '#4f46e5' }} />
          <p style={{ fontSize: '.9rem' }}>Carregando Gerenciador de EAD...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <style>{`
        .ecad-input-premium {
          width: 100%;
          height: 38px;
          padding: 0 12px;
          border-radius: 8px;
          border: 1px solid var(--border-color, #cbd5e1);
          background: var(--bg-card, #ffffff);
          color: var(--text-main, #1e293b);
          font-size: 0.85rem;
          transition: all 0.2s ease;
          outline: none;
        }
        .ecad-input-premium:focus {
          border-color: var(--primary, #4f46e5);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
        }
        .ecad-input-premium:disabled {
          opacity: 0.6;
          background: var(--bg-main, #f1f5f9);
          cursor: not-allowed;
        }

        .ecad-textarea-premium {
          width: 100%;
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid var(--border-color, #cbd5e1);
          background: var(--bg-card, #ffffff);
          color: var(--text-main, #1e293b);
          font-size: 0.85rem;
          transition: all 0.2s ease;
          outline: none;
          resize: vertical;
        }
        .ecad-textarea-premium:focus {
          border-color: var(--primary, #4f46e5);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
        }

        .ecad-select-premium {
          width: 100%;
          height: 38px;
          padding: 0 12px;
          border-radius: 8px;
          border: 1px solid var(--border-color, #cbd5e1);
          background: var(--bg-card, #ffffff);
          color: var(--text-main, #1e293b);
          font-size: 0.85rem;
          transition: all 0.2s ease;
          outline: none;
          cursor: pointer;
        }
        .ecad-select-premium:focus {
          border-color: var(--primary, #4f46e5);
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
        }

        .hover-upload:hover {
          background: #e2e8f0 !important;
          border-color: #94a3b8 !important;
        }
        
        .topico-drag-card {
          cursor: grab;
          transition: border-color 0.2s, background-color 0.2s;
        }
        .topico-drag-card:active {
          cursor: grabbing;
        }
        .topico-drag-over {
          border-color: var(--primary, #4f46e5) !important;
          background-color: rgba(79, 70, 229, 0.03) !important;
        }

        .aula-drag-row {
          cursor: grab;
          transition: background-color 0.2s;
        }
        .aula-drag-row:active {
          cursor: grabbing;
        }
        .aula-drag-over {
          background-color: rgba(79, 70, 229, 0.05) !important;
        }
        .table-row-hover:hover {
          background: var(--bg-main, #f8fafc);
        }
        
        .perfil-drag-row {
          cursor: grab;
          transition: border-color 0.2s, background-color 0.2s;
        }
        .perfil-drag-row:active {
          cursor: grabbing;
        }
        .perfil-drag-over {
          border-color: var(--primary, #4f46e5) !important;
          background-color: rgba(79, 70, 229, 0.05) !important;
          box-shadow: 0 0 0 1px var(--primary, #4f46e5) !important;
        }

        .hover-card {
          transition: all 0.2s ease;
        }
        .hover-card:hover {
          border-color: var(--primary, #4f46e5) !important;
          background: var(--bg-card, #ffffff) !important;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.08);
          transform: translateY(-2px);
        }
      `}</style>
      
      {/* Container Principal Nativo do Administrador */}
      <div className="ac-admin-data-card" style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 'calc(100vh - 160px)' }}>
        
        {/* Topbar de Filtros e Busca */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => navigate('/master/dashboard')}
              className="action-btn"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border-color)', color: 'var(--text-main)', cursor: 'pointer' }}
              title="Voltar ao Painel"
            >
              <ArrowLeft size={14} />
            </button>
            <div style={{ fontWeight: 700, fontSize: '.95rem', color: 'var(--text-main)' }}>
              EAD - Cadastrar e Organizar Cursos
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Buscar por aulas ou tópicos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ height: 32, width: 240, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px 0 30px', fontSize: '.82rem', background: 'var(--bg-card)', color: 'var(--text-main)' }}
              />
            </div>
          </div>
        </div>

        {/* Corpo com Split Sidebar / Conteúdo */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          
          {/* Sidebar Interno de Perfis */}
          <div style={{ width: 280, borderRight: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-main, #f8fafc)', padding: 12, display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 8, marginBottom: 6 }}>
              <span style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Perfis Selecionados</span>
              <button
                type="button"
                onClick={openNewPerfil}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary, #4f46e5)', display: 'flex', alignItems: 'center', padding: 2 }}
                title="Novo Perfil"
              >
                <Plus size={14} />
              </button>
            </div>
            {[...catalog].sort((a, b) => (a.ordem || 0) - (b.ordem || 0)).map((perfil) => {
              const id = perfil.id;
              const isSelected = selectedPerfilId === id;
              const Icon = getPerfilIcon(id);
              const totalAulas = perfil.topicos?.reduce((s, t) => s + (t.subtopicos?.length || 0), 0) || 0;

              return (
                <button
                  key={id}
                  draggable={true}
                  onDragStart={(e) => handlePerfilDragStart(e, id)}
                  onDragOver={(e) => handlePerfilDragOver(e)}
                  onDrop={(e) => {
                    e.currentTarget.classList.remove('perfil-drag-over');
                    handlePerfilDrop(e, id);
                  }}
                  onDragEnter={(e) => {
                    e.currentTarget.classList.add('perfil-drag-over');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('perfil-drag-over');
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.classList.remove('perfil-drag-over');
                  }}
                  className="perfil-drag-row"
                  onClick={() => {
                    setSelectedPerfilId(id);
                    setExpandedTopicoId(null);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: 'none',
                    background: isSelected ? 'var(--bg-card, white)' : 'transparent',
                    color: isSelected ? 'var(--primary, #4f46e5)' : 'var(--text-main)',
                    boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.05), 0 0 0 1px var(--border-color)' : 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: isSelected ? 'rgba(79, 70, 229, 0.08)' : 'var(--bg-card)',
                    color: isSelected ? 'var(--primary, #4f46e5)' : 'var(--text-muted)'
                  }}>
                    <Icon size={14} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.82rem', fontWeight: isSelected ? 700 : 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getPerfilLabel(perfil)}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {totalAulas} {totalAulas === 1 ? 'aula' : 'aulas'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Área Central de Conteúdo */}
          <div className="ac-admin-list-scroll" style={{ flex: 1, padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {activePerfil ? (
              <>
                {/* Banner Descritivo do Perfil */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <h2 style={{ fontSize: '.92rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>
                        Roteiros de Aprendizado para {getPerfilLabel(activePerfil)}
                      </h2>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          type="button"
                          onClick={() => openEditPerfil(activePerfil)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary, #4f46e5)', padding: 2 }}
                          title="Editar Perfil"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm({ tipo: 'PERFIL', id: activePerfil.id, titulo: activePerfil.nome })}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}
                          title="Excluir Perfil"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <p style={{ fontSize: '.76rem', color: 'var(--text-muted)', margin: '2px 0 0 0' }}>{getPerfilDesc(activePerfil)}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '.74rem', fontWeight: 600, padding: '2px 8px', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 20, color: 'var(--text-main)' }}>
                      {activePerfil.topicos.length} Módulo(s)
                    </span>
                    <button
                      type="button"
                      onClick={openNewTopico}
                      className="btn-primary"
                      style={{ height: 26, padding: '0 10px', fontSize: '.72rem', display: 'flex', alignItems: 'center', gap: 4, borderRadius: 6 }}
                    >
                      <Plus size={12} /> Novo Módulo
                    </button>
                  </div>
                </div>

                {/* Lista de Tópicos e Aulas */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {activePerfil.topicos.length === 0 ? (
                    <div style={{ padding: 48, textAlign: 'center', border: '1px dashed var(--border-color)', borderRadius: 10 }}>
                      <Layers size={28} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
                      <h3 style={{ fontSize: '.88rem', fontWeight: 600, color: 'var(--text-main)', margin: 0 }}>Nenhum módulo cadastrado</h3>
                      <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 4, marginBottom: 12 }}>Os dados locais de desenvolvimento estão limpos. Crie um novo módulo abaixo para começar.</p>
                      <button type="button" onClick={openNewTopico} className="btn-primary" style={{ height: 32, padding: '0 12px', fontSize: '.78rem' }}>
                        Adicionar Novo Módulo
                      </button>
                    </div>
                  ) : (
                    activePerfil.topicos.map((topico, topicoIdx) => {
                      const isExpanded = expandedTopicoId === topico.id;
                      const subtopicos = topico.subtopicos || [];
                      const activeSubtopicos = subtopicos.filter((s) => s.status !== 'REMOVIDO' && s.status !== 'INATIVO');

                      return (
                        <div
                          key={topico.id}
                          draggable={true}
                          onDragStart={(e) => handleTopicoDragStart(e, topico.id)}
                          onDragOver={(e) => handleTopicoDragOver(e)}
                          onDrop={(e) => {
                            e.currentTarget.classList.remove('topico-drag-over');
                            handleTopicoDrop(e, topico.id);
                          }}
                          onDragEnter={(e) => {
                            e.currentTarget.classList.add('topico-drag-over');
                          }}
                          onDragLeave={(e) => {
                            e.currentTarget.classList.remove('topico-drag-over');
                          }}
                          onDragEnd={(e) => {
                            e.currentTarget.classList.remove('topico-drag-over');
                          }}
                          className="topico-drag-card"
                          style={{
                            border: '1px solid var(--border-color, #e5e7eb)',
                            borderRadius: 10,
                            overflow: 'hidden',
                            background: 'var(--bg-card)'
                          }}
                        >
                          {/* Cabeçalho do Tópico (Acordeão) */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px 16px',
                            background: isExpanded ? 'var(--bg-main, #f8fafc)' : 'transparent',
                            borderBottom: isExpanded ? '1px solid var(--border-color)' : 'none',
                            cursor: 'pointer'
                          }}
                          onClick={() => setExpandedTopicoId(isExpanded ? null : topico.id)}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {isExpanded ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />}
                              <span style={{ fontSize: '.7rem', fontWeight: 700, padding: '1px 6px', background: 'rgba(79, 70, 229, 0.08)', borderRadius: 4, color: 'var(--primary, #4f46e5)', border: '1px solid rgba(79, 70, 229, 0.15)' }}>
                                Ordem {topico.ordem}
                              </span>
                              <h3 style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>{topico.titulo}</h3>
                              <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>({activeSubtopicos.length} {activeSubtopicos.length === 1 ? 'aula' : 'aulas'})</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                              {/* Botões de Ordenação */}
                              <button
                                disabled={topicoIdx === 0}
                                onClick={() => moveTopicoOrdem(topico, 'UP')}
                                className="action-btn"
                                style={{ padding: 4, color: topicoIdx === 0 ? 'var(--border-color)' : 'var(--text-muted)', cursor: topicoIdx === 0 ? 'not-allowed' : 'pointer' }}
                                title="Subir Módulo"
                              >
                                <ArrowUp size={15} />
                              </button>
                              <button
                                disabled={topicoIdx === activePerfil.topicos.length - 1}
                                onClick={() => moveTopicoOrdem(topico, 'DOWN')}
                                className="action-btn"
                                style={{ padding: 4, color: topicoIdx === activePerfil.topicos.length - 1 ? 'var(--border-color)' : 'var(--text-muted)', cursor: topicoIdx === activePerfil.topicos.length - 1 ? 'not-allowed' : 'pointer' }}
                                title="Descer Módulo"
                              >
                                <ArrowDown size={15} />
                              </button>

                              <div style={{ width: 1, height: 14, background: 'var(--border-color)', margin: '0 4px' }} />

                              {/* CRUD */}
                              <button
                                onClick={() => openEditTopico(topico)}
                                className="action-btn"
                                style={{ color: 'var(--primary, #4f46e5)' }}
                                title="Editar Módulo"
                              >
                                <Pencil size={15} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm({ tipo: 'TOPICO', id: topico.id, titulo: topico.titulo })}
                                className="action-btn"
                                style={{ color: '#ef4444' }}
                                title="Excluir Módulo"
                              >
                                <Trash2 size={15} />
                              </button>
                              
                              <button
                                onClick={() => openNewAula(topico.id, subtopicos.length)}
                                type="button"
                                className="action-btn"
                                style={{ height: 26, padding: '0 8px', fontSize: '.72rem', display: 'flex', alignItems: 'center', gap: 4, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-card)', color: 'var(--text-main, #334155)' }}
                              >
                                <Plus size={12} /> Aula
                              </button>
                            </div>
                          </div>

                          {/* Seção das Aulas (Lista de Subtópicos) */}
                          {isExpanded && (
                            <div style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
                              {activeSubtopicos.length === 0 ? (
                                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.78rem' }}>
                                  Nenhuma aula cadastrada. Clique em "+ Aula" para criar.
                                </div>
                              ) : (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '.78rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', background: 'var(--bg-main, #f8fafc)' }}>
                                        <th style={{ padding: '8px 16px', width: 60 }}>Ordem</th>
                                        <th style={{ padding: '8px 16px' }}>Nome da Aula / Descrição</th>
                                        <th style={{ padding: '8px 16px', width: 120 }}>Duração</th>
                                        <th style={{ padding: '8px 16px', width: 110 }}>Status</th>
                                        <th style={{ padding: '8px 16px', width: 100 }}></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {activeSubtopicos
                                        .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
                                        .map((aula, aulaIdx) => {
                                          const isDraft = aula.status === 'RASCUNHO' || aula.status === 'INATIVO';
                                          return (
                                            <tr
                                              key={aula.id}
                                              draggable={true}
                                              onDragStart={(e) => handleAulaDragStart(e, aula.id, topico.id)}
                                              onDragOver={(e) => handleAulaDragOver(e)}
                                              onDrop={(e) => {
                                                e.currentTarget.classList.remove('aula-drag-over');
                                                handleAulaDrop(e, aula.id, topico.id);
                                              }}
                                              onDragEnter={(e) => {
                                                if (draggedAula && draggedAula.topicoId === topico.id) {
                                                  e.currentTarget.classList.add('aula-drag-over');
                                                }
                                              }}
                                              onDragLeave={(e) => {
                                                e.currentTarget.classList.remove('aula-drag-over');
                                              }}
                                              onDragEnd={(e) => {
                                                e.currentTarget.classList.remove('aula-drag-over');
                                              }}
                                              className="table-row-hover aula-drag-row"
                                              style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-main)' }}
                                            >
                                              <td style={{ padding: '10px 16px', fontWeight: 700 }}>{aula.ordem}</td>
                                              <td style={{ padding: '10px 16px' }}>
                                                <div style={{ fontWeight: 600 }}>{aula.titulo}</div>
                                                {aula.descricao && <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 2, maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{aula.descricao}</div>}
                                              </td>
                                              <td style={{ padding: '10px 16px' }}>
                                                <div style={{ fontWeight: 600 }}>{aula.duracao || '—'}</div>
                                                <div style={{ marginTop: 4 }}>
                                                  <span style={{
                                                    fontSize: '.66rem',
                                                    padding: '1px 6px',
                                                    borderRadius: 4,
                                                    fontWeight: 700,
                                                    background: aula.dificuldade === 'Avançado' ? 'rgba(239, 68, 68, 0.08)' : aula.dificuldade === 'Intermediário' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                                                    color: aula.dificuldade === 'Avançado' ? '#ef4444' : aula.dificuldade === 'Intermediário' ? '#d97706' : '#059669'
                                                  }}>
                                                    {aula.dificuldade}
                                                  </span>
                                                </div>
                                              </td>
                                              <td style={{ padding: '10px 16px' }}>
                                                <span style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: 3,
                                                  fontSize: '.66rem',
                                                  padding: '1px 6px',
                                                  borderRadius: 4,
                                                  fontWeight: 700,
                                                  background: isDraft ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                                                  color: isDraft ? '#d97706' : '#059669'
                                                }}>
                                                  {isDraft ? <EyeOff size={10} /> : <Eye size={10} />}
                                                  {aula.status}
                                                </span>
                                              </td>
                                              <td style={{ padding: '10px 16px' }}>
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                  <button
                                                    disabled={!aula.videoUrl}
                                                    onClick={() => {
                                                      if (aula.videoUrl) window.open(aula.videoUrl, '_blank');
                                                    }}
                                                    className="action-btn"
                                                    style={{ color: aula.videoUrl ? '#2563eb' : '#000000', opacity: aula.videoUrl ? 1 : 0.4, cursor: aula.videoUrl ? 'pointer' : 'not-allowed' }}
                                                    title={aula.videoUrl ? "Tocar Vídeo" : "Sem vídeo"}
                                                  >
                                                    <Play size={11} />
                                                  </button>
                                                  <button
                                                    onClick={() => openVersoesModal(aula.id, aula.titulo)}
                                                    className="action-btn"
                                                    style={{ color: '#0f766e' }}
                                                    title="Histórico de Versões"
                                                  >
                                                    <History size={11} />
                                                  </button>
                                                  <button
                                                    disabled={publishingAulaId === aula.id}
                                                    onClick={() => handlePublishAula(aula.id, aula.titulo)}
                                                    className="action-btn"
                                                    style={{ color: '#d97706', cursor: publishingAulaId === aula.id ? 'not-allowed' : 'pointer' }}
                                                    title="Publicar para Produção"
                                                  >
                                                    {publishingAulaId === aula.id ? (
                                                      <Loader2 size={11} className="animate-spin" />
                                                    ) : (
                                                      <FileUp size={11} />
                                                    )}
                                                  </button>
                                                  <button
                                                    onClick={() => openEditAula(aula, topico.id)}
                                                    className="action-btn"
                                                    style={{ color: 'var(--primary, #4f46e5)' }}
                                                    title="Editar Aula"
                                                  >
                                                    <Pencil size={11} />
                                                  </button>
                                                  <button
                                                    onClick={() => setDeleteConfirm({ tipo: 'AULA', id: aula.id, titulo: aula.titulo })}
                                                    className="action-btn"
                                                    style={{ color: '#ef4444' }}
                                                    title="Excluir Aula"
                                                  >
                                                    <Trash2 size={11} />
                                                  </button>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Seção de Gravações Não Vinculadas */}
                <div style={{ marginTop: 24, borderTop: '1px solid var(--border-color)', paddingTop: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <Film size={18} style={{ color: 'var(--primary, #4f46e5)' }} />
                    <h3 style={{ fontSize: '.92rem', fontWeight: 700, color: 'var(--text-main)', margin: 0 }}>
                      Gravações Não Vinculadas
                    </h3>
                    <span style={{ fontSize: '.74rem', fontWeight: 600, padding: '2px 8px', background: 'var(--bg-main, #f8fafc)', border: '1px solid var(--border-color)', borderRadius: 20, color: 'var(--text-muted)' }}>
                      {unlinkedVideos.length} vídeo(s)
                    </span>
                  </div>

                  {unlinkedVideos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '8px 12px', background: 'var(--bg-main, #f8fafc)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="checkbox"
                          id="selectAllUnlinked"
                          checked={unlinkedVideos.length > 0 && selectedVideoPaths.length === unlinkedVideos.length}
                          onChange={handleToggleSelectAll}
                          style={{ cursor: 'pointer', width: 16, height: 16 }}
                        />
                        <label htmlFor="selectAllUnlinked" style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-main)', cursor: 'pointer' }}>
                          Selecionar Todos ({unlinkedVideos.length})
                        </label>
                      </div>

                      {selectedVideoPaths.length > 0 && (
                        <button
                          type="button"
                          onClick={handleDeleteSelectedVideos}
                          className="btn-danger"
                          style={{ display: 'flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px', fontSize: '.75rem', borderRadius: 6, background: '#ef4444', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                        >
                          <Trash2 size={13} />
                          <span>Apagar Selecionadas ({selectedVideoPaths.length})</span>
                        </button>
                      )}
                    </div>
                  )}
                  
                  {unlinkedVideos.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', border: '1px dashed var(--border-color)', borderRadius: 8, color: 'var(--text-muted)', fontSize: '.8rem' }}>
                      Nenhuma gravação pendente de vínculo.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                      {unlinkedVideos.map((video) => {
                        const isChecked = selectedVideoPaths.includes(video.path);
                        return (
                          <div
                            key={video.path}
                            onClick={() => handleOpenVincularModal(video)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: 12,
                              background: 'var(--bg-main, #f8fafc)',
                              border: isChecked ? '1px solid var(--primary, #4f46e5)' : '1px solid var(--border-color)',
                              borderRadius: 8,
                              cursor: 'pointer',
                              gap: 10
                            }}
                            className="hover-card"
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleToggleSelectVideo(video.path)}
                              style={{ cursor: 'pointer', width: 16, height: 16 }}
                            />
                            
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <Film size={14} style={{ color: 'var(--text-muted)' }} />
                                <span style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }} title={video.filename}>
                                  {video.filename}
                                </span>
                              </div>
                              <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Clique para vincular</span>
                                <ChevronRight size={12} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-muted)' }}>
                Selecione um perfil de usuário na barra lateral.
              </div>
            )}
          </div>
        </div>
      </div>

      <TopicoModal
        open={topicoModalOpen}
        onClose={() => setTopicoModalOpen(false)}
        editingTopico={editingTopico}
        topicoForm={topicoForm}
        setTopicoForm={setTopicoForm}
        onSubmit={handleSaveTopico}
      />

      <AulaModal
        open={aulaModalOpen}
        onClose={() => setAulaModalOpen(false)}
        editingAula={editingAula}
        aulaForm={aulaForm}
        setAulaForm={setAulaForm}
        disablePdf={disablePdf}
        setDisablePdf={setDisablePdf}
        uploadingVideo={uploadingVideo}
        uploadingPdf={uploadingPdf}
        handleUpload={handleUpload}
        onSubmit={handleSaveAula}
      />

      <PerfilModal
        open={perfilModalOpen}
        onClose={() => setPerfilModalOpen(false)}
        editingPerfil={editingPerfil}
        perfilForm={perfilForm}
        setPerfilForm={setPerfilForm}
        onSubmit={handleSavePerfil}
      />

      <VincularVideoModal
        open={vincularModalOpen}
        onClose={handleCloseVincularModal}
        selectedVideo={selectedVideo}
        vincularForm={vincularForm}
        setVincularForm={setVincularForm}
        catalog={catalog}
        vincularLoading={vincularLoading}
        onConfirm={handleConfirmVincular}
        getPerfilLabel={getPerfilLabel}
      />

      {/* ============================================================== */}
      {/* CONFIRMAÇÃO DE EXCLUSÃO (NATIVA) */}
      {/* ============================================================== */}
      <ConfirmModal
        open={!!deleteConfirm}
        title="Confirmar Exclusão"
        message={
          deleteConfirm
            ? deleteConfirm.tipo === 'PERFIL'
              ? `ATENÇÃO: Deseja realmente excluir o perfil "${deleteConfirm.titulo}"? Esta ação removerá permanentemente o perfil e todos os seus módulos e aulas associados do banco de dados (Cascade).`
              : `Deseja realmente excluir ${deleteConfirm.tipo === 'TOPICO' ? 'o módulo' : 'a aula'} "${deleteConfirm.titulo}"? Os dados do registro serão preservados, mas ele não será mais exibido no player de Cursos (Soft Delete).`
            : ''
        }
        confirmLabel="Excluir"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={executeDelete}
      />

      <VersoesModal
        open={versoesModalOpen}
        onClose={() => setVersoesModalOpen(false)}
        versoesSubtopicoTitulo={versoesSubtopicoTitulo}
        loadingVersoes={loadingVersoes}
        versoesList={versoesList}
        restoringVersao={restoringVersao}
        handleRestoreVersao={handleRestoreVersao}
      />
    </div>
  );
}

export default EcadStudioPageModular;
